import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { DirectionsRenderer } from '@react-google-maps/api';
import Icon from '../Icons/Icon';
import DirectionsSegments from '../VamosMap/DirectionsSegment';
import CommonModal from '../Common/Modal/Modal';
import VamosMap from '../VamosMap';
import SitesInMarkers from '../VamosMap/SitesInMarkers';
import Button from '../Button/Button';
import { useDebouncedClick } from '../../utils/debounce';

const GetDirectionModal = ({
  closeModal, directions, currentRouteStops, routeName,
}) => {
  const [drivingDirectionsOpen, setDrivingDirectionsOpen] = useState(true);
  const [mapTilesLoaded, setMapTilesLoaded] = useState(false);
  const handleGetDirection = () => {
    closeModal();
  };
  const printRef = useRef(null);

  const generatePdf = async () => {
    const element = printRef.current;

    const canvas = await html2canvas(element, {
      useCORS: true, // Enable cross-origin images
      allowTaint: true, // Allow cross-origin images to be loaded
      // logging: true, // Enable logging for debugging
    });

    // Create a new jsPDF instance with letter size
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });

    // Letter size dimensions in mm
    const pageWidth = 215.9;
    const pageHeight = 279.4;

    // Margins and header height
    const margin = 10;
    const headerHeight = 10; // Height of the header

    // Add header text
    pdf.setFontSize(12);
    pdf.text(`Route: ${routeName}`, pageWidth / 2, margin + headerHeight, {
      align: 'center',
    });

    // Calculate the dimensions of the image
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const aspectRatio = imgWidth / imgHeight;

    const pdfWidth = pageWidth - margin * 2; // Subtract margins from width
    const pdfHeight = pdfWidth / aspectRatio; // This is the height of the image maintaining aspect ratio

    // Available height for the image on each page
    const availableHeight = pageHeight - margin * 2 - headerHeight - 5;

    let remainingHeight = imgHeight;
    let pageYOffset = 0;

    while (remainingHeight > 0) {
      // Calculate the height of the current slice
      const sliceHeight = Math.min(remainingHeight, availableHeight * (imgWidth / pdfWidth));

      // Create a canvas for the current slice
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = imgWidth;
      sliceCanvas.height = sliceHeight;

      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(
        canvas,
        0, pageYOffset,
        imgWidth, sliceHeight,
        0, 0,
        imgWidth, sliceHeight,
      );

      const imgSliceData = sliceCanvas.toDataURL('image/png');

      // Add image slice to PDF
      pdf.addImage(imgSliceData, 'PNG', margin, margin + headerHeight + 5, pdfWidth, pdfHeight * (sliceHeight / imgHeight));

      // Update remaining height and y-offset for the next slice
      remainingHeight -= sliceHeight;
      pageYOffset += sliceHeight;

      if (remainingHeight > 0) {
        pdf.addPage();
        // Add header text for the new page
        pdf.text(`Route: ${routeName}`, pageWidth / 2, margin + headerHeight, {
          align: 'center',
        });
      }
    }

    return pdf;
  };

  const handleSaveToPdf = async () => {
    const pdf = await generatePdf();
    pdf.save(`map-${routeName}.pdf`);
  };

  const handlePrint = async () => {
    const pdf = await generatePdf();
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl);

    printWindow.addEventListener('load', () => {
      printWindow.print();
    });
  };

  const [handleClickDebouncedSave, isDebouncedSave] = useDebouncedClick(handleSaveToPdf, 500);
  const [handleClickDebouncedPrint, isDebouncedPrint] = useDebouncedClick(handlePrint, 500);
  // className w-[824] may have to be changed. Was a conflict ana had changed it to w-107
  return (
    <CommonModal title="Directions and Map" handleClose={handleGetDirection} boxClassName="w-[1024px] " titlePosition="left" closeIconPosition="" headerClassName="p-4">
      <div className="w-[824px] mx-auto" id="direction-modal-content">
        <div className="sticky top-0 bg-vamos-white z-10 flex items-center justify-between mb-4 pt-4 pb-2">
          <h2 className="text-2xl font-bold">{routeName}</h2>
          <div className="flex gap-2 h-13">
            <Button
              Icon={<Icon name="download" />}
              color="action"
              onClick={handleClickDebouncedSave}
              size="xl"
              className="min-w-13 flex items-center justify-center"
              isDisabled={!mapTilesLoaded || isDebouncedSave}
            />
            {/* The sharing functionality has been deprioritized until after the launch. */}
            {/* <Button
              Icon={<Icon name="share" />}
              color="action"
              onClick={() => { }}
              size="xl"
              className="min-w-13 flex items-center justify-center"
            /> */}
            <Button
              Icon={<Icon name="print" />}
              color="action"
              onClick={handleClickDebouncedPrint}
              size="xl"
              className="min-w-13 flex items-center justify-center"
              isDisabled={!mapTilesLoaded || isDebouncedPrint}
            />
          </div>
        </div>
        <div ref={printRef}>
          <VamosMap className="h-fit rounded-md overflow-hidden h-60vh" onTilesLoaded={() => setMapTilesLoaded(true)}>
            <SitesInMarkers sitesArray={currentRouteStops} />
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
              }}
            />
          </VamosMap>

          <div className="mt-3 mb-3 border-vamos-blue-gray border rounded p-2">
            <button
              className="flex items-center gap-2 text-vamos-green4 font-bold pl-4"
              onClick={() => { setDrivingDirectionsOpen(!drivingDirectionsOpen); }}
              type="button"
            >
              Driving Directions
              {drivingDirectionsOpen
                ? <Icon name="expanded" />
                : <Icon name="collapse" />}
            </button>
            {directions && drivingDirectionsOpen && <DirectionsSegments directions={directions} />}
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default GetDirectionModal;

GetDirectionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  directions: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentRouteStops: PropTypes.arrayOf(PropTypes.object).isRequired,
  routeName: PropTypes.string.isRequired,
};
